/**
 * Клиентский пресет конструкции: номинал кВт (base) vs строка каталога для труб (construction).
 * baseKey — минимальный номинал ≥ кВт (поле ввода не меняется).
 * Если Q_факт заметно выше табличного Q строки baseKey — construction = ceil по таблице.
 */

import { normalizeClientThermalDeltaTemp } from './clientPresetReferenceDelta';
import { isClientFlowM3hOverCatalogTable } from './clientPresetCatalogFlow';

/** Допуск при сравнении Q_факт с табличным расходом пресета (м³/ч). */
export const CLIENT_CATALOG_FLOW_TOLERANCE_M3H = 0.1;

/** Как в UI и в таблицах пресетов (0,01 м³/ч). */
export function roundClientCatalogFlowM3h(qM3h) {
  const n = Number(qM3h);
  if (!Number.isFinite(n)) return n;
  return parseFloat(n.toFixed(2));
}

function tableFlowFitsClientFlowM3h(qFactM3h, tableFlowM3h) {
  const q = roundClientCatalogFlowM3h(qFactM3h);
  const tq = roundClientCatalogFlowM3h(tableFlowM3h);
  if (!Number.isFinite(q) || !Number.isFinite(tq)) return false;
  return q <= tq + CLIENT_CATALOG_FLOW_TOLERANCE_M3H;
}

function isRowValidForShape(row, shape) {
  if (!row || row.flowRate == null) return false;
  if (shape === 'square' && !row.squareProfile) return false;
  return Number.isFinite(Number(row.flowRate));
}

/**
 * Минимальный ключ пресета, у которого табличный flowRate ≥ qFactM3h.
 * @param {(stepKw: number, row: object) => boolean} [extraStepOk]
 */
export function clientCatalogPresetKeyCeilByTableFlowM3h(
  qFactM3h,
  shape,
  paramsTable,
  powerSteps,
  extraStepOk
) {
  const q = roundClientCatalogFlowM3h(qFactM3h);
  if (!Number.isFinite(q) || q <= 0) return null;

  let bestKey = null;
  let bestTableQ = null;

  for (const stepKw of powerSteps) {
    const row = paramsTable[stepKw];
    if (!isRowValidForShape(row, shape)) continue;
    if (typeof extraStepOk === 'function' && !extraStepOk(stepKw, row)) continue;
    const tq = Number(row.flowRate);
    if (!tableFlowFitsClientFlowM3h(q, tq)) continue;
    if (bestTableQ == null || tq < bestTableQ) {
      bestTableQ = tq;
      bestKey = stepKw;
    }
  }

  if (bestKey != null) return bestKey;

  for (let i = powerSteps.length - 1; i >= 0; i--) {
    const stepKw = powerSteps[i];
    const row = paramsTable[stepKw];
    if (!isRowValidForShape(row, shape)) continue;
    if (typeof extraStepOk === 'function' && !extraStepOk(stepKw, row)) continue;
    return stepKw;
  }
  return null;
}

/** Внутренний ключ строки каталога: минимальный номинал кВт ≥ введённой мощности (поле ввода не меняется). */
export function clientCatalogPowerBaseKeyKw(rawPowerKw, powerSteps, maxStepKw) {
  const p = Number(rawPowerKw);
  if (!Number.isFinite(p) || p <= 0) return null;
  if (p > maxStepKw) return null;
  return powerSteps.find((s) => s >= p) ?? maxStepKw;
}

export function resolveConstructionKeyFromBaseAndQFact({
  baseKeyKw,
  qFactM3h,
  shape,
  paramsTable,
  powerSteps,
  extraStepOk,
}) {
  const q = roundClientCatalogFlowM3h(qFactM3h);
  if (!Number.isFinite(q) || q <= 0) return null;

  const ceilKey = () =>
    clientCatalogPresetKeyCeilByTableFlowM3h(q, shape, paramsTable, powerSteps, extraStepOk);

  if (baseKeyKw == null) return ceilKey();

  const row = paramsTable[baseKeyKw];
  if (!isRowValidForShape(row, shape)) return ceilKey();
  if (typeof extraStepOk === 'function' && !extraStepOk(baseKeyKw, row)) return ceilKey();

  const rowQ = Number(row.flowRate);
  if (tableFlowFitsClientFlowM3h(q, rowQ)) return baseKeyKw;
  return ceilKey();
}

/**
 * @param {(powerKw: number, deltaTemp: number) => number} calcFlowFromPowerKw
 */
export function resolveClientConstructionFromThermalInput({
  inputMode,
  powerKw,
  flowRateM3h,
  deltaTemp,
  shape,
  paramsTable,
  powerSteps,
  maxStepKw,
  maxTableFlowM3h,
  calcFlowFromPowerKw,
  extraStepOk = undefined,
}) {
  const dt = normalizeClientThermalDeltaTemp(deltaTemp);
  let qFact = null;
  let baseKeyKw = null;

  if (inputMode === 'power') {
    const p = Number(powerKw);
    if (!Number.isFinite(p) || p <= 0) return null;
    qFact = roundClientCatalogFlowM3h(calcFlowFromPowerKw(p, dt));
    if (!Number.isFinite(qFact) || qFact <= 0) return null;
    if (isClientFlowM3hOverCatalogTable(qFact, maxTableFlowM3h)) {
      return {
        overTable: true,
        qFactM3h: qFact,
        baseKeyKw: p > maxStepKw ? null : clientCatalogPowerBaseKeyKw(p, powerSteps, maxStepKw),
        constructionKeyKw: null,
        sizingFlowM3h: qFact,
      };
    }
    baseKeyKw = clientCatalogPowerBaseKeyKw(p, powerSteps, maxStepKw);
  } else {
    qFact = roundClientCatalogFlowM3h(flowRateM3h);
    if (!Number.isFinite(qFact) || qFact <= 0) return null;
    if (isClientFlowM3hOverCatalogTable(qFact, maxTableFlowM3h)) {
      return {
        overTable: true,
        qFactM3h: qFact,
        baseKeyKw: null,
        constructionKeyKw: null,
        sizingFlowM3h: qFact,
      };
    }
  }

  const constructionKeyKw = resolveConstructionKeyFromBaseAndQFact({
    baseKeyKw,
    qFactM3h: qFact,
    shape,
    paramsTable,
    powerSteps,
    extraStepOk,
  });

  if (constructionKeyKw == null) return null;

  const sizingFlowM3h = Number(paramsTable[constructionKeyKw]?.flowRate);
  return {
    overTable: false,
    qFactM3h: qFact,
    baseKeyKw,
    constructionKeyKw,
    sizingFlowM3h: Number.isFinite(sizingFlowM3h) ? sizingFlowM3h : qFact,
  };
}
